// src/lib/observability/sentry.ts
import { Capacitor } from "@capacitor/core";
import * as Sentry from "@sentry/capacitor";
import * as SentryReact from "@sentry/react";

let started = false;

/** Initialize Sentry on the native app only. No-op on web / when no DSN is set. */
export function initSentry(): void {
  if (started) return;
  const dsn = process.env.NEXT_PUBLIC_SENTRY_DSN;
  if (!Capacitor.isNativePlatform() || !dsn) return;
  started = true;
  Sentry.init(
    {
      dsn,
      environment: process.env.NEXT_PUBLIC_APP_ENV ?? "dev",
      release: `gounique@${process.env.NEXT_PUBLIC_APP_VERSION ?? "dev"}`,
      // Crash/error reporting only — no performance tracing in v1.
      tracesSampleRate: 0,
      // MapLibre GL fix: Sentry's default browserApiErrors + breadcrumbs
      // integrations globally monkey-patch fetch / XMLHttpRequest / timers /
      // requestAnimationFrame. That interferes with MapLibre GL's tile + style
      // network requests and render loop, so the map never loads its data and
      // renders washed-out / stuck on a loading state (device-verified: removing
      // both restores the map; removing only one does not). We drop both.
      // Crash reporting is unaffected: globalHandlers still captures uncaught
      // errors + unhandled rejections, and the native SDK captures native
      // crashes / ANRs. Only the auto JS breadcrumb trail + wrapped-callback
      // error context are lost.
      integrations: (defaults) =>
        defaults.filter((i) => i.name !== "BrowserApiErrors" && i.name !== "Breadcrumbs"),
    },
    SentryReact.init,
  );
}

/** Attach (or clear) the Supabase user id on Sentry events. No email / PII. */
export function setSentryUser(id: string | null): void {
  if (!Capacitor.isNativePlatform()) return;
  Sentry.setUser(id ? { id } : null);
}
